import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createReminder, updateReminder } from '../../Redux/Slice/ReminderSlice';
import { toast } from 'react-toastify';
import {
  Bell,
  Calendar,
  Clock,
  PhoneCall,
  AlertTriangle,
  Tag,
  FileText,
  AlignLeft,
  Search,
  Check,
  X,
  User,
  ChevronRight,
  Hash,
  Users,
  Globe,
} from 'lucide-react';
import moment from 'moment';
import axios from 'axios';
import Cookies from 'js-cookie';
import './CreateReminderModal.css';

const BaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Low', color: '#10b981', bg: '#ecfdf5' },
  { value: 'Medium', label: 'Medium', color: '#3b82f6', bg: '#eff6ff' },
  { value: 'High', label: 'High', color: '#f59e0b', bg: '#fffbeb' },
  { value: 'Urgent', label: 'Urgent', color: '#ef4444', bg: '#fef2f2' },
];

const REMINDER_TYPE_GROUPS = [
  {
    group: 'Support & Client Operations',
    options: [
      { value: 'Call Follow-up', label: '📞 Call Follow-up (Client Call)' },
      { value: 'Support Ticket', label: '🎫 Support Ticket (Customer Support)' },
      { value: 'Client Visit', label: '🚗 Client Visit (On-Site Visit)' },
      { value: 'Client Training', label: '🎓 Client Training (Module Training)' },
      { value: 'Customer Query', label: '💬 Customer Query (Client Feedback)' },
    ],
  },
  {
    group: 'Development & Technical Tasks',
    options: [
      { value: 'Bug / Error Fix', label: '🐛 Bug / Error Fix (Issue Resolution)' },
      { value: 'Software Installation', label: '💻 Software Installation (Setup/Deployment)' },
      { value: 'Module Update', label: '📦 Module Update (Feature / Release)' },
      { value: 'Task Deadline', label: '⏳ Task Deadline (Milestone / Delivery)' },
      { value: 'DB Sync & Backup', label: '🔄 DB Sync & Backup (Database Check)' },
    ],
  },
  {
    group: 'Management & Accounts',
    options: [
      { value: 'Payment / AMC Follow-up', label: '💰 Payment / AMC Follow-up (Billing/Renewal)' },
      { value: 'Escalation', label: '🚨 Escalation (Senior Developer / Management)' },
      { value: 'Team Meeting', label: '👥 Team Meeting (Internal Review)' },
      { value: 'General', label: '📌 General Task / Note' },
    ],
  },
];

const CreateReminderModal = ({ show, onHide, editData, onSuccess }) => {
  const dispatch = useDispatch();
  const { LoginData } = useSelector((state) => state.auth);
  const cookieUser =
    Cookies.get('IDQUA') ||
    (Cookies.get('loginData') ? JSON.parse(Cookies.get('loginData'))?.data?._id : null);
  const currentUserId = LoginData?.data?._id || cookieUser;
  const currentUserName = LoginData?.data?.Name || (Cookies.get('loginData') ? JSON.parse(Cookies.get('loginData'))?.data?.Name : 'You');
  const currentUserPhoto = LoginData?.data?.ProfilePhoto || (Cookies.get('loginData') ? JSON.parse(Cookies.get('loginData'))?.data?.ProfilePhoto : null);
  const currentUserRole = LoginData?.data?.Role || (Cookies.get('loginData') ? JSON.parse(Cookies.get('loginData'))?.data?.Role : 'Support');

  // Team members from chat slice or API
  const { members } = useSelector((state) => state.chat || { members: [] });
  const [teamMembers, setTeamMembers] = useState(members || []);
  const [assignMode, setAssignMode] = useState('self'); // 'self', 'custom', 'all'
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const memberSearchRef = useRef(null);

  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [reminderType, setReminderType] = useState('Call Follow-up');
  const [assignedTo, setAssignedTo] = useState([]);
  const [callNo, setCallNo] = useState('');
  const [complaintId, setComplaintId] = useState(null);
  const [selectedCallDetails, setSelectedCallDetails] = useState(null);

  // Call search state
  const [callSearchQuery, setCallSearchQuery] = useState('');
  const [callSuggestions, setCallSuggestions] = useState([]);
  const [isSearchingCalls, setIsSearchingCalls] = useState(false);
  const [showCallDropdown, setShowCallDropdown] = useState(false);
  const callSearchRef = useRef(null);

  const [loading, setLoading] = useState(false);

  // Load team members on modal open if not already available
  useEffect(() => {
    if (show) {
      if (!members || members.length === 0) {
        axios
          .get(`${BaseUrl}/Chat/members`)
          .then((res) => {
            if (res.data?.data) {
              setTeamMembers(res.data.data);
            }
          })
          .catch((err) => console.error('Failed to load team members:', err));
      } else {
        setTeamMembers(members);
      }
    }
  }, [show, members]);

  // Click outside to close call and member search dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (callSearchRef.current && !callSearchRef.current.contains(e.target)) {
        setShowCallDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Safe Company Name extractor
  const getCompanyName = (comp) => {
    if (!comp) return 'Customer Support';
    if (typeof comp === 'object') {
      return comp.WhaClientName || comp.CompanyName || 'Customer Support';
    }
    return String(comp);
  };

  // Initialize or reset form values
  useEffect(() => {
    if (!show) return;

    if (editData) {
      setTitle(editData.title || '');
      setDescription(editData.description || '');
      setReminderDate(editData.reminderDate || moment().format('YYYY-MM-DD'));
      setReminderTime(editData.reminderTime || moment().format('HH:mm'));
      setPriority(editData.priority || 'Medium');
      setReminderType(editData.reminderType || 'Call Follow-up');
      const assignees = Array.isArray(editData.assignedTo)
        ? editData.assignedTo.map((u) => (typeof u === 'object' ? u._id : u))
        : [];
      if (editData.assignMode === 'all') {
        setAssignMode('all');
        setAssignedTo(teamMembers.length > 0 ? teamMembers.map((m) => m._id) : assignees);
      } else if (assignees.length === 0 || (assignees.length === 1 && assignees[0] === currentUserId)) {
        setAssignMode('self');
        setAssignedTo(currentUserId ? [currentUserId] : []);
      } else {
        setAssignMode('custom');
        setAssignedTo(assignees);
      }
      setCallNo(editData.callNo || '');
      setCallSearchQuery(editData.callNo || '');
      setComplaintId(editData.complaintId?._id || (typeof editData.complaintId === 'string' ? editData.complaintId : null));
      if (editData.complaintId) {
        const c = typeof editData.complaintId === 'object' ? editData.complaintId : {};
        setSelectedCallDetails({
          ...c,
          CompanyName: getCompanyName(c.CompanyName),
        });
      }
    } else {
      // Default: 1 minute in the future
      const istFuture = moment().add(1, 'minute');
      setTitle('');
      setDescription('');
      setReminderDate(istFuture.format('YYYY-MM-DD'));
      setReminderTime(istFuture.format('HH:mm'));
      setPriority('Medium');
      setReminderType('Call Follow-up');
      setAssignMode('self');
      setAssignedTo(currentUserId ? [currentUserId] : []);
      setCallNo('');
      setCallSearchQuery('');
      setComplaintId(null);
      setSelectedCallDetails(null);
    }
  }, [show, editData, currentUserId, teamMembers]);

  // Merge current user into team members list and keep current user at the TOP
  const allMembersWithSelf = useMemo(() => {
    const list = [...(teamMembers || [])];
    if (currentUserId && !list.some((m) => String(m._id) === String(currentUserId))) {
      list.unshift({
        _id: currentUserId,
        Name: currentUserName,
        Role: currentUserRole || 'Self',
        ProfilePhoto: currentUserPhoto,
      });
    }
    return list.sort((a, b) => {
      if (String(a._id) === String(currentUserId)) return -1;
      if (String(b._id) === String(currentUserId)) return 1;
      return (a.Name || '').localeCompare(b.Name || '');
    });
  }, [teamMembers, currentUserId, currentUserName, currentUserRole, currentUserPhoto]);

  const handleToggleMember = (memberId) => {
    setAssignedTo((prev) => {
      if (prev.some((id) => String(id) === String(memberId))) {
        return prev.filter((id) => String(id) !== String(memberId));
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleRemoveMember = (memberId) => {
    setAssignedTo((prev) => prev.filter((id) => String(id) !== String(memberId)));
  };

  const handleAssignModeChange = (mode) => {
    setAssignMode(mode);
    if (mode === 'self') {
      setAssignedTo(currentUserId ? [currentUserId] : []);
    } else if (mode === 'all') {
      if (allMembersWithSelf.length > 0) {
        setAssignedTo(allMembersWithSelf.map((m) => m._id));
      } else {
        setAssignedTo(currentUserId ? [currentUserId] : []);
      }
    } else if (mode === 'custom') {
      // Always ensure current user ("khud") is preserved in custom selection
      setAssignedTo((prev) => {
        if (currentUserId && !prev.some((id) => String(id) === String(currentUserId))) {
          return [currentUserId, ...prev];
        }
        return prev.length > 0 ? prev : (currentUserId ? [currentUserId] : []);
      });
    }
  };

  const filteredMembers = allMembersWithSelf.filter((m) => {
    if (!memberSearchQuery.trim()) return true;
    const q = memberSearchQuery.toLowerCase();
    return (
      (m.Name && m.Name.toLowerCase().includes(q)) ||
      (m.Role && m.Role.toLowerCase().includes(q)) ||
      (m.Email && m.Email.toLowerCase().includes(q))
    );
  });

  // Debounced call search
  useEffect(() => {
    if (!callSearchQuery || !showCallDropdown) {
      setCallSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchingCalls(true);
      try {
        const res = await axios.get(`${BaseUrl}/Reminder/search-calls?query=${encodeURIComponent(callSearchQuery)}`);
        if (res.data?.data) {
          setCallSuggestions(res.data.data);
        }
      } catch (err) {
        console.error('Failed to search calls:', err);
      } finally {
        setIsSearchingCalls(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [callSearchQuery, showCallDropdown]);

  const handleSelectCall = (item) => {
    setCallNo(item.CallNo);
    setComplaintId(item._id);
    setSelectedCallDetails({
      ...item,
      CompanyName: getCompanyName(item.CompanyName),
    });
    setCallSearchQuery(item.CallNo);
    setShowCallDropdown(false);
  };

  const handleClearCall = () => {
    setCallNo('');
    setComplaintId(null);
    setSelectedCallDetails(null);
    setCallSearchQuery('');
  };

  const formatCallDateTime = (dateVal, idVal, timeVal) => {
    let timeObj = null;

    if (idVal) {
      try {
        const idStr = String(idVal);
        if (idStr.length >= 8) {
          const timestamp = parseInt(idStr.substring(0, 8), 16) * 1000;
          const fromId = new Date(timestamp);
          if (!isNaN(fromId.getTime())) {
            timeObj = fromId;
          }
        }
      } catch (e) { }
    }

    if (!timeObj && dateVal) {
      const fallback = new Date(dateVal);
      if (!isNaN(fallback.getTime())) {
        timeObj = fallback;
      }
    }

    let formattedDate = '';
    if (dateVal && typeof dateVal === 'string' && dateVal.includes('/') && dateVal.split('/').length === 3) {
      formattedDate = dateVal;
    } else if (dateVal && typeof dateVal === 'string' && dateVal.length >= 10 && dateVal.includes('-')) {
      const ymd = dateVal.slice(0, 10).split('-');
      if (ymd.length === 3) {
        formattedDate = `${ymd[2]}/${ymd[1]}/${ymd[0]}`;
      }
    } else if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
      formattedDate = moment(dateVal).format('DD/MM/YYYY');
    } else if (timeObj) {
      formattedDate = moment(timeObj).format('DD/MM/YYYY');
    }

    let formattedTime = timeVal || '';
    if (!formattedTime && timeObj) {
      formattedTime = moment(timeObj).format('hh:mm A');
    }

    return {
      date: formattedDate,
      time: formattedTime,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Please enter a reminder title.');
      return;
    }

    if (!reminderDate || !reminderTime) {
      toast.error('Please select both reminder date and time.');
      return;
    }

    setLoading(true);

    try {
      let finalAssignedUsers = [];
      if (assignMode === 'self') {
        finalAssignedUsers = currentUserId ? [currentUserId] : [];
      } else if (assignMode === 'all') {
        finalAssignedUsers = allMembersWithSelf.length > 0 ? allMembersWithSelf.map((m) => m._id) : (currentUserId ? [currentUserId] : []);
      } else {
        finalAssignedUsers = assignedTo.length > 0 ? [...assignedTo] : (currentUserId ? [currentUserId] : []);
        // Always include creator ("khud") so both creator and assigned members receive the reminder
        if (currentUserId && !finalAssignedUsers.some((id) => String(id) === String(currentUserId))) {
          finalAssignedUsers.unshift(currentUserId);
        }
      }

      const payload = {
        title: title.trim(),
        description: description.trim(),
        reminderDate,
        reminderTime,
        priority,
        reminderType,
        callNo: callNo.trim(),
        complaintId,
        assignMode,
        assignedTo: finalAssignedUsers,
        userId: currentUserId,
      };

      if (editData && editData._id) {
        await dispatch(updateReminder({ id: editData._id, data: payload })).unwrap();
        toast.success('Reminder updated successfully!');
      } else {
        await dispatch(createReminder(payload)).unwrap();
        toast.success('Reminder created successfully!');
      }

      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      toast.error(err || 'Failed to save reminder.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 1055 }}
    >
      <div className="modal-dialog modal-dialog-centered reminder-create-dialog">
        <div className="modal-content reminder-modal-content">
          {/* Header */}
          <div className="modal-header reminder-modal-header d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div className="reminder-header-icon">
                <Bell size={22} />
              </div>
              <div>
                <h5 className="reminder-modal-title">
                  {editData ? 'Edit Reminder' : 'Add a Reminder'}
                </h5>
                <div className="reminder-modal-subtitle">
                  Schedule real-time support alerts & call follow-ups
                </div>
              </div>
            </div>
            <button
              type="button"
              className="reminder-close-btn"
              onClick={onHide}
              disabled={loading}
              aria-label="Close"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="modal-body reminder-modal-body">
              {/* Reminder Title */}
              <div className="reminder-form-group">
                <label className="reminder-label">
                  <span className="reminder-label-left">
                    <FileText size={15} className="reminder-label-icon" />
                    <span>Reminder Title</span>
                    <span className="reminder-required-star">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  className="reminder-input"
                  placeholder="e.g., Follow-up with client regarding server migration..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              {/* Description / Notes */}
              <div className="reminder-form-group">
                <label className="reminder-label">
                  <span className="reminder-label-left">
                    <AlignLeft size={15} className="reminder-label-icon" />
                    <span>Description / Notes</span>
                  </span>
                  <span className="reminder-label-hint">Optional</span>
                </label>
                <textarea
                  className="reminder-textarea"
                  rows="2"
                  placeholder="Add specific instructions, contact notes, or checklist..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Date & Time Row */}
              <div className="row g-3 reminder-form-group">
                <div className="col-12 col-sm-6">
                  <label className="reminder-label">
                    <span className="reminder-label-left">
                      <Calendar size={15} className="reminder-label-icon" />
                      <span>Reminder Date</span>
                      <span className="reminder-required-star">*</span>
                    </span>
                  </label>
                  <input
                    type="date"
                    className="reminder-input"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    required
                  />
                </div>
                <div className="col-12 col-sm-6">
                  <label className="reminder-label">
                    <span className="reminder-label-left">
                      <Clock size={15} className="reminder-label-icon" />
                      <span>Reminder Time</span>
                      <span className="reminder-required-star">*</span>
                    </span>
                  </label>
                  <input
                    type="time"
                    className="reminder-input"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Priority & Reminder Type Row */}
              <div className="row g-3 reminder-form-group">
                <div className="col-12 col-sm-6">
                  <label className="reminder-label">
                    <span className="reminder-label-left">
                      <AlertTriangle size={15} style={{ color: '#f59e0b' }} />
                      <span>Priority</span>
                    </span>
                  </label>
                  <div className="priority-btn-grid">
                    {PRIORITY_OPTIONS.map((p) => {
                      const isSelected = priority === p.value;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          className={'priority-tab-btn' + (isSelected ? ' active' : '')}
                          style={{
                            backgroundColor: isSelected ? p.bg : '#ffffff',
                            borderColor: isSelected ? p.color : '#e2e8f0',
                            color: isSelected ? p.color : '#64748b',
                            fontWeight: isSelected ? '700' : '500',
                            boxShadow: isSelected ? ('0 2px 6px ' + p.color + '25') : 'none',
                          }}
                          onClick={() => setPriority(p.value)}
                        >
                          <span
                            className="priority-dot"
                            style={{ backgroundColor: p.color }}
                          />
                          <span>{p.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="col-12 col-sm-6">
                  <label className="reminder-label">
                    <span className="reminder-label-left">
                      <Tag size={15} className="reminder-label-icon" />
                      <span>Reminder Type</span>
                    </span>
                  </label>
                  <select
                    className="reminder-select"
                    value={reminderType}
                    onChange={(e) => setReminderType(e.target.value)}
                  >
                    {REMINDER_TYPE_GROUPS.map((grp) => (
                      <optgroup key={grp.group} label={grp.group}>
                        {grp.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assign Team Member(s) */}
              <div className="reminder-form-group position-relative" ref={memberSearchRef}>
                <div className="reminder-label mb-2">
                  <span className="reminder-label-left">
                    <Users size={15} className="reminder-label-icon" />
                    <span>Assign Team Member(s)</span>
                  </span>
                  <span className="reminder-label-hint">
                    {assignMode === 'self'
                      ? 'Assigned to You'
                      : assignMode === 'all'
                        ? ('All Support Team (' + allMembersWithSelf.length + ')')
                        : (assignedTo.length + ' Member' + (assignedTo.length !== 1 ? 's' : '') + ' Selected')}
                  </span>
                </div>

                {/* Segmented Control Bar */}
                <div className="reminder-segmented-control mb-2">
                  <button
                    type="button"
                    className={'segmented-tab-btn' + (assignMode === 'self' ? ' active' : '')}
                    onClick={() => handleAssignModeChange('self')}
                  >
                    <User size={13} />
                    <span>Only Me</span>
                  </button>
                  <button
                    type="button"
                    className={'segmented-tab-btn' + (assignMode === 'custom' ? ' active' : '')}
                    onClick={() => handleAssignModeChange('custom')}
                  >
                    <Users size={13} />
                    <span>Select Members {assignedTo.length > 0 ? ('(' + assignedTo.length + ')') : ''}</span>
                  </button>
                  <button
                    type="button"
                    className={'segmented-tab-btn' + (assignMode === 'all' ? ' active' : '')}
                    onClick={() => handleAssignModeChange('all')}
                  >
                    <Globe size={13} />
                    <span>All Team ({allMembersWithSelf.length})</span>
                  </button>
                </div>

                {/* Custom Member Selection Area */}
                {assignMode === 'custom' && (
                  <div className="custom-member-selector-panel">
                    {/* Selected Member Chips */}
                    {assignedTo.length > 0 && (
                      <div className="selected-chips-container">
                        {assignedTo.map((mId) => {
                          const isSelf = String(mId) === String(currentUserId);
                          const mInfo = allMembersWithSelf.find((m) => String(m._id) === String(mId)) || {
                            _id: mId,
                            Name: isSelf ? (currentUserName + ' (You)') : 'Team Member',
                          };
                          const displayName = isSelf ? ((mInfo.Name || currentUserName) + ' (You)') : mInfo.Name;
                          return (
                            <div
                              key={mId}
                              className={'member-chip ' + (isSelf ? 'chip-self' : 'chip-member')}
                            >
                              {mInfo.ProfilePhoto ? (
                                <img src={mInfo.ProfilePhoto} alt="" className="member-chip-avatar" />
                              ) : (
                                <span className={'member-chip-initial ' + (isSelf ? 'initial-self' : '')}>
                                  {(displayName || 'U').charAt(0)}
                                </span>
                              )}
                              <span>{displayName}</span>
                              <button
                                type="button"
                                className="member-chip-remove"
                                onClick={() => handleRemoveMember(mId)}
                                title="Remove"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Member Search Input */}
                    <div className="member-search-container">
                      <div className="member-search-icon-wrap">
                        <Search size={14} />
                      </div>
                      <input
                        type="text"
                        className="member-search-input"
                        placeholder="Search team members by name or role..."
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                      />
                      {memberSearchQuery && (
                        <button
                          type="button"
                          className="member-search-clear-btn"
                          onClick={() => setMemberSearchQuery('')}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>

                    {/* Integrated Scrollable Member Selection List */}
                    <div className="member-selection-list">
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map((member) => {
                          const isSelf = String(member._id) === String(currentUserId);
                          const isSelected = assignedTo.some((id) => String(id) === String(member._id));
                          return (
                            <button
                              key={member._id}
                              type="button"
                              className={'member-list-item ' + (isSelected ? 'selected' : '')}
                              onClick={() => handleToggleMember(member._id)}
                            >
                              <div className="d-flex align-items-center gap-2.5">
                                {member.ProfilePhoto ? (
                                  <img
                                    src={member.ProfilePhoto}
                                    alt=""
                                    className="member-item-avatar"
                                  />
                                ) : (
                                  <div className={'member-item-initial ' + (isSelf ? 'initial-self' : '')}>
                                    {(member.Name || 'U').charAt(0)}
                                  </div>
                                )}
                                <div className="d-flex flex-column text-start">
                                  <span className="member-item-name">
                                    {member.Name}
                                    {isSelf && (
                                      <span className="badge-self">You (Self)</span>
                                    )}
                                  </span>
                                  <span className="member-item-role">
                                    {member.Role}
                                  </span>
                                </div>
                              </div>
                              <div className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input member-checkbox"
                                  checked={isSelected}
                                  readOnly
                                />
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="member-empty-message">
                          No team members found matching "{memberSearchQuery}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Token Number Assignment */}
              <div className="reminder-form-group position-relative" ref={callSearchRef}>
                <div className="reminder-label mb-2">
                  <span className="reminder-label-left">
                    <PhoneCall size={15} style={{ color: '#10b981' }} />
                    <span>Token / Call Follow-up</span>
                  </span>
                  {callNo && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0 text-decoration-none fw-semibold"
                      style={{ fontSize: '11.5px' }}
                      onClick={handleClearCall}
                    >
                      Remove Token
                    </button>
                  )}
                </div>

                {/* Selected Call Preview Card */}
                {callNo && selectedCallDetails ? (
                  <div className="selected-token-preview-card mb-2">
                    {/* Top Row: Token Badge, Status Badge, and Change Button */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="badge fw-bold d-inline-flex align-items-center gap-1.5"
                          style={{
                            backgroundColor: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            fontSize: '12.5px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            letterSpacing: '0.3px',
                          }}
                        >
                          <Hash size={13} />
                          Token #{callNo}
                        </span>

                        {selectedCallDetails.Status && (
                          <span
                            className="badge rounded-pill fw-semibold"
                            style={{
                              fontSize: '11px',
                              padding: '4px 10px',
                              backgroundColor:
                                selectedCallDetails.Status === 'Completed'
                                  ? '#ecfdf5'
                                  : selectedCallDetails.Status === 'In Progress'
                                    ? '#eff6ff'
                                    : '#fffbeb',
                              color:
                                selectedCallDetails.Status === 'Completed'
                                  ? '#059669'
                                  : selectedCallDetails.Status === 'In Progress'
                                    ? '#2563eb'
                                    : '#d97706',
                              border: `1px solid ${selectedCallDetails.Status === 'Completed'
                                  ? '#a7f3d0'
                                  : selectedCallDetails.Status === 'In Progress'
                                    ? '#bfdbfe'
                                    : '#fde68a'
                                }`,
                            }}
                          >
                            {selectedCallDetails.Status}
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1 fw-semibold"
                        style={{ fontSize: '11.5px' }}
                        onClick={handleClearCall}
                      >
                        Change
                      </button>
                    </div>

                    {/* Middle Row: Company Name */}
                    <div
                      className="fw-bold text-dark mb-1"
                      style={{ fontSize: '13.5px', letterSpacing: '-0.1px' }}
                    >
                      {getCompanyName(selectedCallDetails.CompanyName)}
                    </div>

                    {/* Requirement / Issue Details */}
                    {selectedCallDetails.Requirement && (
                      <div
                        className="p-2 rounded-2 mb-2 text-secondary"
                        style={{
                          fontSize: '12px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e2e8f0',
                          lineHeight: '1.45',
                        }}
                      >
                        <FileText size={12} className="me-1.5 text-primary opacity-75" />
                        <span>{selectedCallDetails.Requirement}</span>
                      </div>
                    )}

                    {/* Bottom Row: Contact Person + Date & Time */}
                    {(() => {
                      const dt = formatCallDateTime(selectedCallDetails.Date, selectedCallDetails._id, selectedCallDetails.Time);
                      return (
                        <div
                          className="d-flex align-items-center justify-content-between pt-1 border-top border-secondary border-opacity-10"
                          style={{ fontSize: '11.5px', color: '#64748b' }}
                        >
                          <span>
                            <User size={12} className="me-1 text-muted" />
                            Contact: <strong className="text-dark">{selectedCallDetails.ContactPerName ? selectedCallDetails.ContactPerName.trim() : 'N/A'}</strong>
                          </span>

                          {(dt.date || dt.time) && (
                            <span className="d-flex align-items-center gap-1.5">
                              {dt.date && (
                                <span className="d-inline-flex align-items-center gap-1 text-muted">
                                  <Calendar size={12} className="text-muted" />
                                  <span>{dt.date}</span>
                                </span>
                              )}
                              {dt.date && dt.time && <span className="text-muted mx-0.5">•</span>}
                              {dt.time && (
                                <span className="d-inline-flex align-items-center gap-1 fw-semibold text-primary">
                                  <Clock size={12} />
                                  <span>{dt.time}</span>
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    <div className="token-search-input-wrap">
                      <div className="token-search-icon">
                        <Hash size={15} />
                      </div>
                      <input
                        type="text"
                        className="reminder-input token-input-field"
                        placeholder="Enter Token No. (e.g. 8595) or search company name..."
                        value={callSearchQuery}
                        onChange={(e) => {
                          setCallSearchQuery(e.target.value);
                          setCallNo(e.target.value);
                          setShowCallDropdown(true);
                        }}
                        onFocus={() => setShowCallDropdown(true)}
                      />
                      {callSearchQuery && (
                        <button
                          type="button"
                          className="btn btn-sm text-muted position-absolute end-0 top-50 translate-middle-y me-2 border-0 bg-transparent p-1"
                          onClick={handleClearCall}
                          title="Clear"
                          style={{ zIndex: 5 }}
                        >
                          <X size={15} />
                        </button>
                      )}
                    </div>

                    {/* Suggestions List */}
                    {showCallDropdown && callSearchQuery && (
                      <div className="token-suggestions-card animate__animated animate__fadeIn animate__faster">
                        <div className="token-suggestions-header">
                          <span className="d-flex align-items-center gap-1.5">
                            <Hash size={12} className="text-primary" />
                            <span>
                              {isSearchingCalls
                                ? 'Searching tokens...'
                                : `Found ${callSuggestions.length} Token${callSuggestions.length !== 1 ? 's' : ''}`}
                            </span>
                          </span>
                          {!isSearchingCalls && callSuggestions.length > 0 && (
                            <span className="text-muted" style={{ fontSize: '11px' }}>
                              Scroll to browse all ({callSuggestions.length})
                            </span>
                          )}
                        </div>

                        <div
                          className="token-suggestions-scroll-area"
                          onWheel={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                        >
                          {isSearchingCalls ? (
                            <div className="p-4 text-center text-muted" style={{ fontSize: '12px' }}>
                              <span className="spinner-border spinner-border-sm me-2 text-primary" role="status" />
                              Searching matching tokens...
                            </div>
                          ) : callSuggestions.length > 0 ? (
                            callSuggestions.map((call) => (
                              <button
                                key={call._id}
                                type="button"
                                className="token-item-btn"
                                onClick={() => handleSelectCall(call)}
                              >
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="d-flex align-items-center gap-2 min-w-0">
                                    <span className="token-pill-badge">
                                      <Hash size={11} />
                                      {call.CallNo}
                                    </span>
                                    <span className="token-company-heading">
                                      {getCompanyName(call.CompanyName)}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                                    <span
                                      className="badge rounded-pill px-2 py-0.5"
                                      style={{
                                        fontSize: '10.5px',
                                        backgroundColor:
                                          call.Status === 'Completed'
                                            ? '#ecfdf5'
                                            : call.Status === 'In Progress'
                                              ? '#eff6ff'
                                              : '#fffbeb',
                                        color:
                                          call.Status === 'Completed'
                                            ? '#059669'
                                            : call.Status === 'In Progress'
                                              ? '#2563eb'
                                              : '#d97706',
                                        border: `1px solid ${call.Status === 'Completed'
                                            ? '#a7f3d0'
                                            : call.Status === 'In Progress'
                                              ? '#bfdbfe'
                                              : '#fde68a'
                                          }`,
                                      }}
                                    >
                                      {call.Status}
                                    </span>
                                    <span className="token-select-action">
                                      Select <ChevronRight size={11} />
                                    </span>
                                  </div>
                                </div>

                                {call.Requirement && (
                                  <div className="token-req-box">
                                    <FileText size={12} className="text-secondary flex-shrink-0 opacity-75" />
                                    <span className="text-truncate">{call.Requirement}</span>
                                  </div>
                                )}

                                <div className="token-meta-footer">
                                  <span>
                                    <User size={11} className="me-1 text-muted" />
                                    Contact: <strong className="text-dark fw-semibold">{call.ContactPerName ? call.ContactPerName.trim() : 'N/A'}</strong>
                                  </span>
                                  {(() => {
                                    const dt = formatCallDateTime(call.Date, call._id, call.Time);
                                    if (!dt.date && !dt.time) return null;
                                    return (
                                      <span className="d-flex align-items-center gap-1.5" style={{ fontSize: '11px' }}>
                                        {dt.date && (
                                          <span className="d-inline-flex align-items-center gap-1 text-muted">
                                            <Calendar size={11} />
                                            <span>{dt.date}</span>
                                          </span>
                                        )}
                                        {dt.date && dt.time && <span className="text-black-50">•</span>}
                                        {dt.time && (
                                          <span className="d-inline-flex align-items-center gap-1 text-primary fw-semibold">
                                            <Clock size={11} />
                                            <span>{dt.time}</span>
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="p-4 text-center text-muted" style={{ fontSize: '12px' }}>
                              No tokens found matching "<strong>{callSearchQuery}</strong>"
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer reminder-modal-footer">
              <button
                type="button"
                className="btn btn-reminder-cancel"
                onClick={onHide}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-reminder-save"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    <span>{editData ? 'Update Reminder' : 'Save Reminder'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateReminderModal;
